import { Box } from "@mui/material";
import { IS_SAFARI } from "app/appConstants";
import { MprcReactPlayer, MprcReactPlayerRef } from "components/MprcReactPlayer";
import { CustomVideoSlide } from "components/types/lightBox";
import React, { useEffect, useMemo, useState } from "react";
import { OnProgressProps } from "react-player/base";

////////////////////////////////////////////////////////////////////////////////

type ShortVideoPlayerControlProps = {
    editMode: boolean;
    startTime?: number;
    endTime?: number;
    height?: number | string;
};

type ShortVideoPlayerProps = Omit<CustomVideoSlide, "width" | "height"> & ShortVideoPlayerControlProps;

export const ShortVideoPlayer = (props: ShortVideoPlayerProps) => {

    const { id, videoId, url, poster, mimetype, isAudio, isHls, editMode, autoPlay, startTime, endTime, height, tracks, onProgress, onEnded } = props;
    const videoRef = React.useRef<MprcReactPlayerRef>(null!);
    const [ready, setReady] = React.useState<boolean>(false);
    const [playing, setPlay] = useState<boolean>(!editMode);

    const handlePause = () => {
        if (editMode) setPlay(false);
    };

    const handlePlay = () => {
        if (editMode) setPlay(true);
    };

    const handleReady = () => {
        setReady(true);
    };

    const sourceUrls = useMemo(() => {
        return url;
    }, [url, mimetype]);

    useEffect(() => {
        if (ready) {
            if (editMode) {
                setPlay(autoPlay ?? false);
            }
            else {
                videoRef.current.seekTo(startTime!);
                setPlay(true);
            }
        }
        else setPlay(false);
    }, [ready, editMode]);

    /** Reset Player */
    useEffect(() => {
        setReady(false);
    }, [id]);

    return (
        <Box
            id="videoPlayerWrapper"
            mb={1}
            height={height}
            position="relative"
        >
            <MprcReactPlayer
                key={id}
                id={id}
                videoId={videoId}
                ref={videoRef}
                playing={playing ?? false}
                playsinline={true}
                stopOnUnmount={true}
                url={sourceUrls}
                width="100%"
                height="100%"
                controls={{
                    seek: editMode,
                    volume: true,
                    speed: false,
                    closeCaption: !editMode,
                    playTime: editMode,
                    fullscreen: false,
                }}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={() => onEnded && onEnded(id, videoRef.current.getDuration())}
                onReady={handleReady}
                onProgress={
                    (state: OnProgressProps) => {
                        if (editMode) {
                            if (onProgress) onProgress(id, state);
                        }
                        else {
                            if (endTime != null && state.playedSeconds > endTime) {
                                setPlay(false);
                                onEnded && onEnded(id, state.playedSeconds);
                            }
                        }
                    }
                }
                config={{
                    file: {
                        attributes: {
                            poster: poster,
                            width: "100%",
                            height: "100%",
                            preload: "auto",
                            disablePictureInPicture: true,
                            controlsList: "noplaybackrate nodownload nofullscreen",
                            crossOrigin: "anonymous"
                        },
                        forceAudio: isAudio,
                        forceHLS: isHls && !IS_SAFARI,
                        tracks: tracks ?? []
                    }
                }}
            />

        </Box>
    );
};

/////////////////////////////////////////////////////////////////////////////////
